import { useMemo, useState } from "react"
import { Modal } from "../../ui/Modal.tsx"
import { StrategyBrowser } from "../strategies/StrategyBrowser.tsx"
import { addIndicator } from "../workspace/workspaceStore.ts"
import { useIndicatorModules } from "./indicatorModules.ts"
import { searchIndicators } from "./indicatorSearch.ts"
import { nextInstanceStyle } from "./indicatorStyle.ts"

export type IndicatorsDialogTab = "indicators" | "strategies"

/**
 * TradingView's "Indicators" dialog: search the built-in indicators and add them
 * to every chart on screen (they share one set), or put a strategy on the active
 * chart for the Strategy Tester.
 */
export function IndicatorsDialog({ initialTab = "indicators", onClose }: {
  readonly initialTab?: IndicatorsDialogTab
  readonly onClose: () => void
}) {
  const [tab, setTab] = useState<IndicatorsDialogTab>(initialTab)
  const [query, setQuery] = useState("")
  const q = query.trim().toLowerCase()

  return (
    <Modal title="Indicators & strategies" onClose={onClose} wide>
      <nav className="dialog-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "indicators"} onClick={() => setTab("indicators")}>
          Indicators
        </button>
        <button type="button" role="tab" aria-selected={tab === "strategies"} onClick={() => setTab("strategies")}>
          Strategies
        </button>
      </nav>
      <input
        autoFocus
        type="search"
        className="dialog-search"
        placeholder="Search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {tab === "indicators" ? <IndicatorBrowser query={q} /> : <StrategyBrowser query={q} onPicked={onClose} />}
    </Modal>
  )
}

function IndicatorBrowser({ query }: { readonly query: string }) {
  const modules = useIndicatorModules(true)
  const [category, setCategory] = useState<string | null>(null)
  const [added, setAdded] = useState<ReadonlyArray<string>>([])

  const categories = useMemo(() => [...new Set(modules?.catalog.map((e) => e.category))].sort(), [modules])
  const results = useMemo(
    () =>
      searchIndicators((modules?.catalog ?? []).filter((entry) => category === null || entry.category === category), query),
    [modules, query, category]
  )

  return (
    <div className="indicator-browser">
      <nav className="indicator-categories">
        <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)}>All</button>
        {categories.map((name) => (
          <button key={name} type="button" aria-pressed={category === name} onClick={() => setCategory(name)}>
            {name}
          </button>
        ))}
      </nav>
      <ul className="indicator-list">
        {modules === null && <li className="muted">Loading…</li>}
        {modules !== null && results.length === 0 && <li className="muted">No indicators match.</li>}
        {results.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              title={entry.description}
              onClick={() => {
                // A second EMA (any overlay) takes the next colour, so the copies can be told apart.
                addIndicator(entry.id, (existing) => nextInstanceStyle(entry, existing))
                setAdded((list) => [...list, entry.id])
              }}
            >
              <span>{entry.name}</span>
              {added.includes(entry.id) && <span className="added">added</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
